import React, { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { MessageSquare, Search, Send, ArrowLeft } from "lucide-react";

const ROLE_LABEL = {
  receptionist: "Reception", doctor: "Doctor", nurse: "Nurse", lab_tech: "Lab", radiologist: "Radiology",
  pharmacist: "Pharmacy", cashier: "Billing", hospital_admin: "Hospital Admin", cfo: "Finance", super_admin: "Admin",
};

function initials(name = "") {
  return name.split(" ").filter(Boolean).slice(0, 2).map((n) => n[0]).join("").toUpperCase();
}

function formatTime(ts) {
  if (!ts) return "";
  const d = new Date(ts);
  const now = new Date();
  const same = d.toDateString() === now.toDateString();
  return same ? d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : d.toLocaleDateString([], { day: "2-digit", month: "short" });
}

export default function Messages() {
  const { user } = useAuth();
  const [coworkers, setCoworkers] = useState([]);
  const [threads, setThreads] = useState([]);
  const [activeThread, setActiveThread] = useState(null); // { thread_id, other }
  const [messages, setMessages] = useState([]);
  const [query, setQuery] = useState("");
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [filter, setFilter] = useState("all"); // all | unread | read
  const listRef = useRef(null);
  const pollRef = useRef(null);

  const loadDirectory = async () => {
    try {
      const [{ data: cw }, { data: t }] = await Promise.all([
        api.get("/messages/coworkers"),
        api.get("/messages/threads"),
      ]);
      setCoworkers(cw || []);
      setThreads(t || []);
    } catch { /* ignore */ }
  };

  const loadMessages = async (threadId) => {
    try {
      const { data } = await api.get(`/messages/threads/${threadId}/messages`);
      setMessages(data || []);
      setTimeout(() => listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: "smooth" }), 50);
    } catch { /* ignore */ }
  };

  useEffect(() => { loadDirectory(); }, []);

  // Poll for new messages / threads every 3s (Messenger-style near-realtime)
  useEffect(() => {
    pollRef.current = setInterval(() => {
      loadDirectory();
      if (activeThread) loadMessages(activeThread.thread_id);
    }, 3000);
    return () => clearInterval(pollRef.current);
  }, [activeThread?.thread_id]);

  const openConversation = async (coworker) => {
    try {
      const { data } = await api.post("/messages/threads", { with_user_id: coworker.user_id });
      const withOther = { ...data, other: coworker };
      setActiveThread(withOther);
      await loadMessages(data.thread_id);
      loadDirectory();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not open conversation"); }
  };

  const send = async () => {
    const body = draft.trim();
    if (!body || !activeThread) return;
    setSending(true);
    try {
      const { data: msg } = await api.post(`/messages/threads/${activeThread.thread_id}/messages`, { body });
      setMessages((m) => [...m, msg]);
      setDraft("");
      setTimeout(() => listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: "smooth" }), 50);
      loadDirectory();
    } catch { toast.error("Failed to send"); }
    finally { setSending(false); }
  };

  const filtered = coworkers.filter((c) => c.name.toLowerCase().includes(query.toLowerCase()) || (ROLE_LABEL[c.role] || c.role).toLowerCase().includes(query.toLowerCase()));

  // Sort: coworkers with existing threads first (by last_at desc), then alphabetical
  const threadByUser = Object.fromEntries((threads || []).map((t) => [t.other?.user_id, t]));
  filtered.sort((a, b) => {
    const ta = threadByUser[a.user_id]?.last_at || "";
    const tb = threadByUser[b.user_id]?.last_at || "";
    if (ta && tb) return tb.localeCompare(ta);
    if (ta) return -1;
    if (tb) return 1;
    return a.name.localeCompare(b.name);
  });

  // Apply read/unread filter (Messenger-style tabs at bottom of contact list)
  const visibleContacts = filtered.filter((c) => {
    const t = threadByUser[c.user_id];
    if (filter === "unread") return (t?.unread || 0) > 0;
    if (filter === "read")   return !!t && !(t.unread > 0);   // conversations we've read
    return true;
  });

  const totalUnread = (threads || []).reduce((s, t) => s + (t.unread || 0), 0);

  return (
    <div className="h-[calc(100vh-64px)] flex" data-testid="messages-page">
      {/* Left pane: contacts */}
      <div className={`${activeThread ? "hidden lg:flex" : "flex"} flex-col w-full lg:w-80 border-r border-slate-200 bg-white`}>
        <div className="p-4 border-b border-slate-200">
          <div className="flex items-center justify-between gap-2 mb-3">
            <div className="flex items-center gap-2">
              <MessageSquare className="w-4 h-4 text-[#1976D2]" />
              <div className="font-semibold text-slate-800">Message Portal</div>
            </div>
            {totalUnread > 0 && (
              <span data-testid="header-unread-total" className="text-[11px] font-bold bg-[#D32F2F] text-white rounded-full px-2 py-0.5">
                {totalUnread} new
              </span>
            )}
          </div>
          <div className="relative">
            <Search className="w-4 h-4 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
            <input data-testid="messages-search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search coworkers…" className="w-full h-9 pl-8 pr-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1976D2]/40" />
          </div>
        </div>
        <div className="overflow-y-auto flex-1">
          {visibleContacts.length === 0 ? (
            <div className="p-6 text-center text-sm text-slate-500">
              {filter === "unread" ? "No unread messages 🎉" : filter === "read" ? "No previously read conversations." : "No coworkers match your search."}
            </div>
          ) : visibleContacts.map((c) => {
            const t = threadByUser[c.user_id];
            const isActive = activeThread?.other?.user_id === c.user_id;
            const isUnread = (t?.unread || 0) > 0;
            return (
              <button key={c.user_id} data-testid={`contact-${c.user_id}`} onClick={() => openConversation(c)} className={`w-full text-left px-4 py-3 border-b border-slate-50 flex items-center gap-3 hover:bg-slate-50 ${isActive ? "bg-[#e6f0fb]" : ""} ${isUnread ? "bg-[#f0f7ff]" : ""}`}>
                <div className="relative flex-shrink-0">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center text-xs font-semibold ${isUnread ? "bg-[#1976D2] text-white" : "bg-slate-100 text-slate-600"}`}>{initials(c.name)}</div>
                  {isUnread && <span className="absolute -top-0.5 -right-0.5 w-3 h-3 rounded-full bg-[#D32F2F] ring-2 ring-white" />}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <div className={`text-sm truncate ${isUnread ? "font-bold text-slate-900" : "font-medium text-slate-700"}`}>{c.name}</div>
                    {t?.last_at && <div className={`text-[10px] tabular flex-shrink-0 ${isUnread ? "font-bold text-[#1976D2]" : "text-slate-400"}`}>{formatTime(t.last_at)}</div>}
                  </div>
                  <div className="flex items-center justify-between gap-2">
                    <div className={`text-xs truncate ${isUnread ? "font-semibold text-slate-800" : "text-slate-500"}`}>{t?.last_message ? t.last_message : (ROLE_LABEL[c.role] || c.role)}</div>
                    {isUnread && <span data-testid={`unread-${c.user_id}`} className="text-[10px] font-bold bg-[#1976D2] text-white rounded-full px-2 py-0.5 min-w-[20px] text-center">{t.unread}</span>}
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {/* Messenger-style bottom filter tabs */}
        <div className="border-t border-slate-200 bg-white p-2 flex gap-1" data-testid="messages-filter-tabs">
          {[
            ["all", "All", coworkers.length],
            ["unread", "Unread", (threads || []).filter((t) => t.unread > 0).length],
            ["read", "Read", (threads || []).filter((t) => !(t.unread > 0)).length],
          ].map(([k, label, count]) => (
            <button key={k} data-testid={`filter-${k}`} onClick={() => setFilter(k)} className={`flex-1 h-9 rounded-lg text-xs font-semibold transition-colors flex items-center justify-center gap-1.5 ${filter === k ? "bg-[#1976D2] text-white" : "text-slate-600 hover:bg-slate-100"}`}>
              {label} {count > 0 && <span className={`text-[10px] px-1.5 rounded-full ${filter === k ? "bg-white/25" : k === "unread" && count > 0 ? "bg-[#D32F2F] text-white" : "bg-slate-200 text-slate-600"}`}>{count}</span>}
            </button>
          ))}
        </div>
      </div>

      {/* Right pane: conversation */}
      <div className={`${activeThread ? "flex" : "hidden lg:flex"} flex-col flex-1 bg-slate-50`}>
        {!activeThread ? (
          <div className="flex-1 flex items-center justify-center text-center p-8">
            <div>
              <MessageSquare className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <div className="font-semibold text-slate-800">Select a coworker to start chatting</div>
              <div className="text-sm text-slate-500 mt-1">Direct messages are private between you and the recipient. Never share Protected Health Information without patient consent.</div>
            </div>
          </div>
        ) : (
          <>
            <div className="px-5 py-3 border-b border-slate-200 bg-white flex items-center gap-3">
              <button onClick={() => setActiveThread(null)} className="lg:hidden text-slate-500"><ArrowLeft className="w-4 h-4" /></button>
              <div className="w-9 h-9 rounded-full bg-slate-100 text-slate-600 flex items-center justify-center text-xs font-semibold">{initials(activeThread.other?.name)}</div>
              <div>
                <div className="text-sm font-semibold text-slate-800">{activeThread.other?.name}</div>
                <div className="text-xs text-slate-500">{ROLE_LABEL[activeThread.other?.role] || activeThread.other?.role}</div>
              </div>
            </div>
            <div ref={listRef} className="flex-1 overflow-y-auto px-5 py-4 space-y-2" data-testid="messages-list">
              {messages.length === 0 && <div className="text-center text-sm text-slate-500 py-8">No messages yet. Say hi 👋</div>}
              {messages.map((m) => {
                const mine = m.sender_id === user?.user_id;
                return (
                  <div key={m.message_id} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`msg-${m.message_id}`}>
                    <div className={`max-w-[75%] rounded-2xl px-3.5 py-2 ${mine ? "bg-[#1976D2] text-white rounded-br-sm" : "bg-white border border-slate-200 text-slate-800 rounded-bl-sm"}`}>
                      <div className="text-sm whitespace-pre-wrap break-words">{m.body}</div>
                      <div className={`text-[10px] mt-1 tabular ${mine ? "text-white/70" : "text-slate-400"}`}>{formatTime(m.created_at)}</div>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="border-t border-slate-200 bg-white px-4 py-3 flex items-end gap-2">
              <textarea
                data-testid="message-input"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                placeholder={`Message ${activeThread.other?.name}…`}
                rows={1}
                className="flex-1 resize-none rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1976D2]/40 min-h-[40px] max-h-32"
              />
              <button data-testid="send-message" onClick={send} disabled={sending || !draft.trim()} className="h-10 px-3 rounded-lg bg-[#1976D2] hover:bg-[#1565C0] text-white text-sm font-semibold flex items-center gap-1.5 disabled:opacity-50 flex-shrink-0">
                <Send className="w-4 h-4" /> Send
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
